import type { AdminOrderPaymentCaptureDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { describeCaptureStory, type CaptureStory } from '../../../components/capture';
import { tokens } from '../../../design';

interface StoryPartProps {
  story: CaptureStory;
  /** Shared probe attributes so both branches expose the same test surface. */
  probe: { 'data-testid': string; 'data-phase': string };
}

/**
 * The capture story of ONE listed hold, sized for a queue row.
 *
 * Reuses `describeCaptureStory` — the same presenter the order drawer reads,
 * so the tab and the drawer can never disagree about a row — but not
 * `PaymentCaptureStory`, which is a drawer-sized card: twenty-five of those is
 * a wall, not a queue.
 *
 * ## Two rules carried over intact
 *
 * **`retry_scheduled` gets no alert and no `role="alert"`.** It is the
 * automation working, and dressing it as a problem is the exact misreading
 * that created this ticket. Only `tone === 'attention'` gets a tinted block,
 * and even that one is not an ARIA alert here: the section head already states
 * «Холды без автоматики / нужен оператор», and twenty-five live-region
 * announcements on one poll would be an interruption, not information.
 *
 * **The raw `blockedReason` is printed verbatim** next to (never instead of)
 * the Russian sentence — the nine-value vocabulary is shared with the
 * `[hold.sweep]` log lines precisely so an operator quoting the screen and an
 * engineer grepping the log are talking about the same row.
 */
/** The quiet phases — one muted sentence, no alert of any kind. */
const QuietStory = ({ story, probe }: StoryPartProps): ReactElement => {
  const { t } = useTranslation();
  const sentence = [story.headline, ...story.lines]
    .map((line) => t(line.key, { ...line.values }))
    .join(' ');
  return (
    <Typography
      {...probe}
      data-tone="quiet"
      sx={{ mt: 0.75, fontSize: 11.5, lineHeight: '15px', color: tokens.text.secondary }}
    >
      {sentence}
    </Typography>
  );
};

/** `needs_operator` — the reason is the paragraph, the facts are the meta row. */
const AttentionStory = ({
  story,
  probe,
  blockedReason,
}: StoryPartProps & { blockedReason: string | null }): ReactElement => {
  const { t } = useTranslation();
  const [lead, ...rest] = story.lines;
  const paragraph = t((lead ?? story.headline).key, { ...(lead ?? story.headline).values });
  const error = story.severity === 'error';

  return (
    <Box
      {...probe}
      data-tone="attention"
      data-severity={story.severity ?? 'warning'}
      sx={{
        mt: 1,
        px: 2.25,
        py: 1.5,
        borderRadius: '9px',
        borderLeft: `3px solid ${error ? tokens.state.danger : tokens.state.warning}`,
        background: error ? tokens.state.dangerBg : tokens.state.warningBg,
      }}
    >
      <Typography
        sx={{ fontSize: 11.5, lineHeight: '15px', fontWeight: 500, color: tokens.text.primary }}
      >
        {paragraph}
      </Typography>
      <Box
        sx={{
          mt: 0.75,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '4px 9px',
          fontSize: 10.5,
          lineHeight: '14px',
          color: tokens.text.tertiary,
        }}
      >
        {blockedReason !== null ? (
          <Box
            component="code"
            data-testid="hold-capture-code"
            title={t('orders.capture.story.codeHint')}
            sx={{
              fontFamily: tokens.font.mono,
              fontSize: 10.5,
              px: 1.25,
              py: '1px',
              borderRadius: '5px',
              background: tokens.surface.base,
              border: `1px solid ${tokens.border.default}`,
              color: tokens.text.secondary,
            }}
          >
            {blockedReason}
          </Box>
        ) : null}
        {rest.map((line) => (
          <Box component="span" key={line.key}>
            {t(line.key, { ...line.values })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const HoldCaptureStory = ({
  capture,
  now,
}: {
  capture: AdminOrderPaymentCaptureDto;
  now?: number | undefined;
}): ReactElement | null => {
  const story = describeCaptureStory(capture, now);
  if (story === null) return null;
  const probe = { 'data-testid': 'hold-capture-story', 'data-phase': story.phase };
  return story.tone === 'quiet' ? (
    <QuietStory story={story} probe={probe} />
  ) : (
    <AttentionStory story={story} probe={probe} blockedReason={capture.blockedReason} />
  );
};
